import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Users, Award, BookOpen, Globe, CheckCircle, Clock } from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

const timeline = [
  { year: '2014', title: 'Conception', description: 'CEPRES was conceived during the Ebola outbreak while Dr. Mogana S. Flomo, Jr. pursued his Ph.D. in Ghana.' },
  { year: '2015', title: 'Legal Establishment', description: 'Legally established as the Center for Environmental and Public Health Research. Classes began in May with nearly 200 students.' },
  { year: '2016', title: 'First Graduation', description: 'Graduation of the first 15 students in February. Hosted the International Environmental Research Conference in Gbarnga.' },
  { year: '2017', title: 'Expansion', description: 'Partnership with Armed Forces of Liberia. Expanded community initiatives and environmental volunteer training.' },
  { year: 'Present', title: 'Growing Impact', description: 'Ongoing academic programs in Agriculture, Education, Engineering, Environmental and Health Sciences.' },
];

const values = [
  { icon: Target, title: 'Determination', description: 'Overcoming challenges with resilience and perseverance.' },
  { icon: Heart, title: 'Commitment', description: 'Dedicated to public health and environmental well-being.' },
  { icon: Award, title: 'Excellence', description: 'Striving for the pinnacle of academic distinction.' },
  { icon: Users, title: 'Community', description: 'Actively working to improve health and environmental conditions.' },
  { icon: BookOpen, title: 'Innovation', description: 'Adapting and evolving our educational approaches.' },
  { icon: Globe, title: 'Accessibility', description: 'Providing affordable, accredited education for all.' },
];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.about} url="/about" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            About <span className="text-[#C5A028]">CIU</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            Born from the crucible of the 2014 Ebola crisis, CEPRES International University 
            emerged with a vision to transform healthcare education in Liberia and beyond.
          </p>
        </div>
      </section>

      {/* Mission & Vision - ALU Style */}
      <section data-testid="mission-vision-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Mission */}
            <div className="bg-[#0D4A2E] text-white p-10 lg:p-16">
              <div className="flex items-center gap-4 mb-6">
                <Target className="w-10 h-10 text-[#C5A028]" />
                <h2 className="text-3xl font-bold">Our Mission</h2>
              </div>
              <p className="text-white/80 text-lg leading-relaxed">
                Stimulating Public Health Quality through Research, Training, and Service Improvement. 
                We are committed to providing premier standard tertiary education that meets global 
                educational standards and best practices.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-[#C5A028] text-[#1A1A1A] p-10 lg:p-16">
              <div className="flex items-center gap-4 mb-6">
                <Eye className="w-10 h-10 text-[#0D4A2E]" />
                <h2 className="text-3xl font-bold">Our Vision</h2>
              </div>
              <p className="text-[#1A1A1A]/80 text-lg leading-relaxed">
                To be at the pinnacle of academic distinction, offering interdisciplinary instruction 
                at all levels while providing local and international educational leadership in 
                environmental and public health education.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline - ALU Style */}
      <section data-testid="timeline-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Our Journey</h2>
          <p className="text-[#666666] mb-12 max-w-2xl">
            From crisis to creation, our path has been one of determination and growth
          </p>

          <div className="space-y-6">
            {timeline.map((item, index) => (
              <div
                key={item.year}
                data-testid={`timeline-${item.year}`}
                className="flex gap-6 bg-white p-6 border-l-4 border-[#C5A028] hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-24 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0D4A2E]">{item.year}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0D4A2E] mb-2">{item.title}</h3>
                  <p className="text-[#666666]">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Life Gallery */}
      <section data-testid="gallery-section" className="py-20 bg-[#0D4A2E]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-4">Campus <span className="text-[#C5A028]">Life</span></h2>
          <p className="text-white/70 mb-12 max-w-2xl">
            Experience the vibrant community at CEPRES International University
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Image 1 - Large */}
            <div className="col-span-2 row-span-2 relative group overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/vfz74n9l_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%285%29.jpeg"
                alt="CEPRES Students"
                className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[#0D4A2E]/40 group-hover:bg-transparent transition-all duration-300" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-bold">Student Life</p>
                <p className="text-sm text-white/70">Building lifelong connections</p>
              </div>
            </div>
            
            {/* Image 2 - Grayscale on hover */}
            <div className="relative group overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/mp7kzwiz_WhatsApp%20Image%202026-02-04%20at%2017.35.15%20%284%29.jpeg"
                alt="Community Health Work"
                className="w-full h-48 object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <p className="absolute bottom-2 left-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">Healthcare Training</p>
            </div>
            
            {/* Image 3 - Grayscale on hover */}
            <div className="relative group overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/bnx3f2sc_WhatsApp%20Image%202026-02-04%20at%2017.35.13.jpeg"
                alt="Graduation Ceremony"
                className="w-full h-48 object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <p className="absolute bottom-2 left-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">Graduation Day</p>
            </div>
            
            {/* Image 4 */}
            <div className="relative group overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/ptyry2rh_WhatsApp%20Image%202026-02-04%20at%2017.35.12%20%283%29.jpeg"
                alt="Certificate Presentation"
                className="w-full h-48 object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <p className="absolute bottom-2 left-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">Achievement Recognition</p>
            </div>
            
            {/* Image 5 */}
            <div className="relative group overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/yfrf73c9_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%287%29.jpeg"
                alt="Campus Events"
                className="w-full h-48 object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <p className="absolute bottom-2 left-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">Campus Events</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values - ALU Style Grid */}
      <section data-testid="values-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Our Values</h2>
          <p className="text-[#666666] mb-12 max-w-2xl">The principles that guide everything we do</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-${value.title.toLowerCase()}`}
                className="p-8 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors duration-300"
              >
                <value.icon className="w-10 h-10 text-[#C5A028] mb-4" />
                <h3 className="text-xl font-bold text-[#0D4A2E] mb-2">{value.title}</h3>
                <p className="text-[#666666] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Model - ALU Style */}
      <section data-testid="learning-model-section" className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Flexible <span className="text-[#C5A028]">Learning</span> Tracks
              </h2>
              <p className="text-white/80 mb-8">
                Same curriculum, your schedule. Choose the track that fits your lifestyle.
              </p>
              <div className="space-y-4">
                {[
                  { years: '4 Years', title: 'Traditional Track', desc: '2 semesters/year - standard pace' },
                  { years: '3 Years', title: 'Standard Track', desc: '~3 semesters/year - accelerated' },
                  { years: '2 Years', title: 'Fast Track', desc: '4 semesters/year - intensive' },
                ].map((track, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white/5 border border-white/10">
                    <div className="w-16 h-16 bg-[#C5A028] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-8 h-8 text-[#1A1A1A]" />
                    </div>
                    <div>
                      <p className="font-bold">{track.title} ({track.years})</p>
                      <p className="text-white/60 text-sm">{track.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1716654716589-62219dcd47d3?w=800" 
                alt="Students"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section data-testid="founder-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/jcz8dn4g_WhatsApp%20Image%202026-02-04%20at%2017.35.13%20%281%29.jpeg"
                  alt="Dr. Mogana S. Flomo, Jr."
                  className="w-full h-auto shadow-2xl"
                />
                {/* Gold accent bar */}
                <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-[#C5A028] -z-10" />
              </div>
            </div>
            
            {/* Content */}
            <div>
              <p className="text-[#C5A028] font-medium uppercase tracking-wider mb-2">Meet Our Founder</p>
              <h2 className="text-4xl font-bold text-[#0D4A2E] mb-2">
                Prof. Dr. Mogana S. Flomo, Jr.
              </h2>
              <p className="text-[#666666] text-lg font-medium mb-6">Founder & President, CEPRES International University</p>
              
              <div className="space-y-4 text-[#666666] leading-relaxed">
                <p>
                  With over <strong className="text-[#0D4A2E]">28 years of teaching experience</strong> in Liberia, 
                  Dr. Flomo has dedicated his life to transforming education across West Africa. He taught in all 
                  the high schools in Bong County before becoming a lecturer at Cuttington University, where he 
                  served for many years.
                </p>
                <p>
                  His expertise extends globally through online teaching at various international institutions. 
                  In <strong className="text-[#0D4A2E]">2018</strong>, Dr. Flomo was appointed as the 
                  <strong className="text-[#0D4A2E]"> Minister of Agriculture</strong> for the Republic of Liberia, 
                  where he served for over two years leading transformative initiatives for farmers, smallholder 
                  farmers, and agricultural production across the nation.
                </p>
                <p>
                  Dr. Flomo founded CEPRES during the 2014 Ebola crisis with a vision to provide quality, 
                  accessible higher education in healthcare and agriculture — fields critical to Liberia's development.
                </p>
              </div>
              
              {/* Achievements */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-[#F5F5F5] p-4">
                  <p className="text-3xl font-bold text-[#C5A028]">28+</p>
                  <p className="text-sm text-[#666666]">Years in Education</p>
                </div>
                <div className="bg-[#F5F5F5] p-4">
                  <p className="text-3xl font-bold text-[#C5A028]">2018</p>
                  <p className="text-sm text-[#666666]">Minister of Agriculture</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Join the CIU Family</h2>
          <p className="text-[#666666] mb-8">Be part of our mission to transform healthcare education in Africa.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/programs" className="inline-flex items-center gap-2 bg-[#0D4A2E] text-white px-8 py-4 font-bold hover:bg-[#082D1C] transition-colors duration-200">
              <ArrowRight className="w-5 h-5" />
              EXPLORE PROGRAMS
            </Link>
            <a href="https://cnte.ciu.edu.lr/apply" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200">
              APPLY NOW
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
